import {
  inferInstallationGuidanceHints,
  type InstallationGuidanceHints,
} from './installation-guidance'
import { isArenaNotificationSetupHost } from './arena'

export interface BrowserInstallPromptChoice {
  outcome: 'accepted' | 'dismissed'
  platform: string
}

export interface BrowserInstallPrompt {
  prompt: () => Promise<void>
  userChoice: Promise<BrowserInstallPromptChoice>
}

interface ServiceWorkerContainerAdapter {
  register: (
    scriptURL: string | URL,
    options?: RegistrationOptions,
  ) => Promise<ServiceWorkerRegistration>
  ready: Promise<ServiceWorkerRegistration>
  getRegistration?: (
    clientURL?: string | URL,
  ) => Promise<ServiceWorkerRegistration | undefined>
}

interface NotificationAdapter {
  readonly permission: NotificationPermission
  requestPermission: () => Promise<NotificationPermission>
}

export interface BrowserNotificationBindings {
  hostname: string
  origin: string
  serviceWorker?: ServiceWorkerContainerAdapter
  pushSupported: boolean
  notification?: NotificationAdapter
  installationHints?: InstallationGuidanceHints
  isStandalone: () => boolean
  currentInstallPrompt?: () => BrowserInstallPrompt | null
  listenForInstallPrompt: (
    listener: (prompt: BrowserInstallPrompt | null) => void,
  ) => () => void
  focus: () => void
  open: (path: string) => Window | null
  withWebPushLifecycleLock?: <T>(operation: () => Promise<T>) => Promise<T>
}

export interface BrowserNotificationCapabilities {
  serviceWorker: boolean
  push: boolean
  notification: boolean
  standalone: boolean
  lifecycleLock: boolean
}

export type NotificationPermissionState
  = | NotificationPermission
    | 'unsupported'

export interface BrowserNotificationRuntime {
  readonly hostname: string
  readonly origin: string
  readCapabilities: () => BrowserNotificationCapabilities
  readInstallationHints: () => InstallationGuidanceHints
  notificationPermission: () => NotificationPermissionState
  requestNotificationPermission: () => Promise<NotificationPermissionState>
  registerServiceWorker: (
    scriptURL: string | URL,
    options?: RegistrationOptions,
  ) => Promise<ServiceWorkerRegistration>
  serviceWorkerReady: () => Promise<ServiceWorkerRegistration>
  getPushSubscription: () => Promise<PushSubscription | null>
  subscribeToPush: (
    applicationServerKey: BufferSource,
  ) => Promise<PushSubscription>
  unsubscribeFromPush: () => Promise<boolean>
  closeWebPushNotifications: () => Promise<void>
  withWebPushLifecycleLock: <T>(operation: () => Promise<T>) => Promise<T>
  listenForInstallPrompt: (
    listener: (prompt: BrowserInstallPrompt | null) => void,
  ) => () => void
  currentInstallPrompt: () => BrowserInstallPrompt | null
  focusCurrentWindow: () => void
  openWindow: (path: string) => Window | null
}

export function createBrowserNotificationRuntime(
  bindings: BrowserNotificationBindings,
): BrowserNotificationRuntime {
  const currentServiceWorkerRegistration = async () => {
    if (!bindings.serviceWorker)
      throw new Error('Service Worker is unavailable')
    return bindings.serviceWorker.getRegistration
      ? bindings.serviceWorker.getRegistration('/')
      : bindings.serviceWorker.ready
  }

  return {
    hostname: bindings.hostname,
    origin: bindings.origin,
    readCapabilities: () => ({
      serviceWorker: !!bindings.serviceWorker,
      push: bindings.pushSupported,
      notification: !!bindings.notification,
      standalone: bindings.isStandalone(),
      lifecycleLock: !!bindings.withWebPushLifecycleLock,
    }),
    readInstallationHints: () =>
      bindings.installationHints ?? { platform: 'other', browser: 'other' },
    notificationPermission: () =>
      bindings.notification?.permission ?? 'unsupported',
    requestNotificationPermission: async () =>
      bindings.notification
        ? bindings.notification.requestPermission()
        : 'unsupported',
    registerServiceWorker: (scriptURL, options) => {
      if (!bindings.serviceWorker)
        throw new Error('Service Worker is unavailable')
      return bindings.serviceWorker.register(scriptURL, options)
    },
    serviceWorkerReady: () => {
      if (!bindings.serviceWorker)
        throw new Error('Service Worker is unavailable')
      return bindings.serviceWorker.ready
    },
    getPushSubscription: async () => {
      if (!bindings.pushSupported)
        return null
      const registration = await currentServiceWorkerRegistration()
      return registration?.pushManager.getSubscription() ?? null
    },
    subscribeToPush: async (applicationServerKey) => {
      if (!bindings.serviceWorker)
        throw new Error('Service Worker is unavailable')
      const registration = await bindings.serviceWorker.ready
      return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })
    },
    unsubscribeFromPush: async () => {
      if (!bindings.serviceWorker || !bindings.pushSupported)
        return false
      const registration = await currentServiceWorkerRegistration()
      const subscription = await registration?.pushManager.getSubscription()
      return subscription ? subscription.unsubscribe() : false
    },
    closeWebPushNotifications: async () => {
      if (!bindings.serviceWorker || !bindings.notification)
        return
      const registration = await currentServiceWorkerRegistration()
      const notifications = (await registration?.getNotifications()) ?? []
      for (const notification of notifications) notification.close()
    },
    withWebPushLifecycleLock: (operation) => {
      if (!bindings.withWebPushLifecycleLock) {
        return Promise.reject(
          new Error('Cross-tab Web Push lifecycle locks are unavailable'),
        )
      }
      return bindings.withWebPushLifecycleLock(operation)
    },
    listenForInstallPrompt: listener =>
      bindings.listenForInstallPrompt(listener),
    currentInstallPrompt: () => bindings.currentInstallPrompt?.() ?? null,
    focusCurrentWindow: () => bindings.focus(),
    openWindow: path => bindings.open(path),
  }
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

type BrowserWindow = Window & typeof globalThis

export interface WindowBrowserNotificationRuntimeOptions {
  captureInstallPrompt?: boolean
}

export function createWindowBrowserNotificationRuntime(
  browserWindow: BrowserWindow,
  options: WindowBrowserNotificationRuntimeOptions = {},
): BrowserNotificationRuntime {
  let currentInstallPrompt: BrowserInstallPrompt | null = null
  const installPromptListeners = new Set<
    (prompt: BrowserInstallPrompt | null) => void
  >()

  const updateInstallPrompt = (prompt: BrowserInstallPrompt | null) => {
    currentInstallPrompt = prompt
    for (const listener of installPromptListeners) listener(prompt)
  }

  if (options.captureInstallPrompt ?? true) {
    browserWindow.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault()
      const browserPrompt = event as Event & BrowserInstallPrompt
      let promptInvoked = false
      const clearCapturedPrompt = () => {
        if (currentInstallPrompt === capturedPrompt)
          updateInstallPrompt(null)
      }

      const capturedPrompt: BrowserInstallPrompt = {
        prompt: async () => {
          if (promptInvoked)
            return
          promptInvoked = true
          clearCapturedPrompt()
          await browserPrompt.prompt()
        },
        userChoice: browserPrompt.userChoice,
      }
      updateInstallPrompt(capturedPrompt)
    })

    browserWindow.addEventListener('appinstalled', () => {
      if (currentInstallPrompt)
        updateInstallPrompt(null)
    })
  }

  const notification
    = 'Notification' in browserWindow
      ? {
          get permission() {
            return browserWindow.Notification.permission
          },
          requestPermission: () =>
            browserWindow.Notification.requestPermission(),
        }
      : undefined
  const webLocks = browserWindow.navigator.locks

  return createBrowserNotificationRuntime({
    hostname: browserWindow.location.hostname,
    origin: browserWindow.location.origin,
    serviceWorker:
      'serviceWorker' in browserWindow.navigator
        ? browserWindow.navigator.serviceWorker
        : undefined,
    pushSupported: 'PushManager' in browserWindow,
    notification,
    installationHints: inferInstallationGuidanceHints({
      userAgent: browserWindow.navigator.userAgent,
      platform: browserWindow.navigator.platform,
      maxTouchPoints: browserWindow.navigator.maxTouchPoints,
    }),
    isStandalone: () =>
      browserWindow.matchMedia('(display-mode: standalone)').matches
      || !!(browserWindow.navigator as NavigatorWithStandalone).standalone,
    currentInstallPrompt: () => currentInstallPrompt,
    listenForInstallPrompt: (listener) => {
      installPromptListeners.add(listener)
      if (currentInstallPrompt)
        listener(currentInstallPrompt)
      return () => installPromptListeners.delete(listener)
    },
    focus: () => browserWindow.focus(),
    open: path => browserWindow.open(path, '_blank'),
    withWebPushLifecycleLock: webLocks
      ? operation => webLocks.request('mentiss-web-push-lifecycle', operation)
      : undefined,
  })
}

export const browserNotificationRuntime
  = createWindowBrowserNotificationRuntime(window, {
    captureInstallPrompt: isArenaNotificationSetupHost(),
  })
